/**
 * @author Rahul Nainala
 * @description 3d Twin configuration code to generate 3D
 */
import { connect } from 'react-redux';
import { Editor } from '@monaco-editor/react';
import { useNavigate } from 'react-router-dom';
import { ExportOutlined, PlusOutlined } from '@ant-design/icons';
import defaultConfig from './defaultConfig.json';
import StyledTable from '../../../commons/styles/table';
import React, { useEffect, useRef, useState } from 'react';
import { StyledContentRight } from '../../../commons/styles/layout';
import { Col, Drawer, Form, Input, Select, Row, message, Button, Tabs } from 'antd';
import {
  configurationCall,
  createConfiguration,
  deleteConfiguration,
  MastersTypeData,
  updateConfiguration
} from '../API';

const DigitalTwinConfiguration = (props) => {
  let [form] = Form.useForm();
  let editorRef = useRef(null);
  let monacoRef = useRef(null);
  let navigate = useNavigate();
  let [visible, setVisible] = useState(false);
  let [tableData, setTableData] = useState([]);
  let [selectData, setSelectData] = useState([]);
  let [isEditMode, setIsEditMode] = useState(false);
  let [selectedRecord, setSelectedRecord] = useState(null);
  let [editEditor, setEditEditor] = useState(JSON.stringify({}, null, 2));
  let [translationCache, setTranslationCache] = useState(props.translationCache || []);

  useEffect(() => {
    if (translationCache !== props.translationCache) {
      setTranslationCache(props.translationCache);
    }
  }, [props.translationCache]);

  const translation = (keyword) => {
    return translationCache && translationCache[keyword] ? translationCache[keyword] : keyword;
  };

  useEffect(() => {
    try {
      let fetchData = async () => {
        let data = await configurationCall();
        setTableData(data);
      };
      fetchData();
    } catch (e) {
      console.error('Facing Error Loading Table', e);
    }
  }, [visible]);

  useEffect(() => {
    try {
      let fetchData = async () => {
        let data = await MastersTypeData();
        setSelectData(data);
      };
      fetchData();
    } catch (e) {
      console.error('Facing error while getting masters type', e);
    }
  }, [visible]);

  let handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
  };

  let addNewDigitalTwin = () => {
    try {
      setVisible(true);
      form.resetFields();
      setEditEditor(JSON.stringify(defaultConfig, null, 2));
      setSelectedRecord(null);
      setIsEditMode(false);
    } catch (e) {
      console.error('Unable to add new digital twin', e);
    }
  };

  let onClose = () => {
    try {
      setVisible(false);
    } catch (e) {
      console.error('Unable to close Modal', e);
    }
  };

  let createConfig = async (props) => {
    try {
      await createConfiguration(props);
      onClose();
    } catch (e) {
      console.error('Facing error while creating New Config', e);
    }
  };

  let updateConfig = async (props) => {
    try {
      await updateConfiguration(props);
      onClose();
    } catch (e) {
      console.error('Facing error while Updating Config', e);
    }
  };

  let deleteConfig = async (props) => {
    try {
      await deleteConfiguration(props);
      onClose();
    } catch (e) {
      console.error('Facing error while deleting config', e);
    }
  };

  let handleEditorChange = (value) => {
    setEditEditor(value);
  };

  let handleCreateClick = async () => {
    try {
      let values = await form.validateFields();
      let payload = {
        ...values,
        JSON: JSON.parse(editEditor)
      };
      if (isEditMode && selectedRecord) {
        payload.id = selectedRecord.Id;
        await updateConfig(payload);
      } else {
        await createConfig(payload);
      }
    } catch (error) {
      message.error(translation('Validation Failed:', error));
    }
  };

  let handleDeleteClick = async () => {
    try {
      await deleteConfig(selectedRecord.Id);
    } catch (error) {
      message.error(translation('Deletion Failed:', error));
    }
  };

  let onRowClick = (record) => {
    return {
      onClick: () => {
        setSelectedRecord(record);
        form.setFieldsValue({
          Name: record.Name,
          Type: record.Type
        });
        setEditEditor(JSON.stringify(record.JSON, null, 2));
        setVisible(true);
        setIsEditMode(true);
      }
    };
  };
  const getFilteredData = (type) => {
    return tableData.filter((record) => record.JSON && record.JSON.type === type);
  };
  let columns = [
    {
      title: translation('Digital Twin Name'),
      dataIndex: 'Name',
      key: 'Name',
      render: (text) => <span style={{ cursor: 'pointer' }}>{text}</span>
    },
    {
      title: translation('Type'),
      dataIndex: 'Type',
      key: 'Type'
    },
    {
      title: translation('Status'),
      dataIndex: 'Status',
      key: 'Status'
    },
    {
      title: translation('Action'),
      key: 'Action',
      render: (text, record) => {
        let type = record.JSON.type;
        let payload = {
          Name: record.Name,
          Type: record.Type,
          SiteId: record.SiteId
        };

        switch (type) {
          case 'child':
            return (
              <Button
                type="primary"
                onClick={() => {
                  navigate(`/rubus/3d/${record.Type}/${record.Name}`, { state: payload });
                }}>
                {translation('Redirect')} <ExportOutlined />
              </Button>
            );
          case 'parent':
            return (
              <Button
                type="primary"
                onClick={() => {
                  navigate(`/rubus/DigitalTwin/${record.Type}/${record.Name}`);
                }}>
                {translation('Redirect')}
                <ExportOutlined />
              </Button>
            );
          default:
            return (
              <Button
                type="primary"
                onClick={() => {
                  navigate(`/rubus/DigitalTwinMulti/${record.Type}/${record.Name}`);
                }}>
                {translation('Redirect')}
                <ExportOutlined />
              </Button>
            );
        }
      }
    }
  ];

  const items = [
    {
      key: '1',
      label: 'Hierarchy',
      children: (
        <StyledTable
          size="small"
          columns={columns}
          dataSource={getFilteredData('parent')}
          onRow={onRowClick}
        />
      )
    },
    {
      key: '2',
      label: 'Multi Hierarchy',
      children: (
        <StyledTable
          size="small"
          columns={columns}
          dataSource={getFilteredData('multiParent')}
          onRow={onRowClick}
        />
      )
    },
    {
      key: '3',
      label: 'Digital Twins',
      children: (
        <StyledTable
          size="small"
          columns={columns}
          dataSource={getFilteredData('child')}
          onRow={onRowClick}
        />
      )
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <StyledContentRight>
        <Button type="primary" onClick={addNewDigitalTwin}>
          <PlusOutlined />
          {translation('Add New Digital Twin')}
        </Button>
      </StyledContentRight>
      <Drawer
        title={
          isEditMode
            ? translation('Update Digital Twin Configuration')
            : translation('Create a new Digital Twin configuration')
        }
        width={1500}
        closable
        onClose={onClose}
        open={visible}
        style={{ paddingBottom: 80 }}>
        <Form form={form} layout="vertical" labelCol={{ span: 4 }} wrapperCol={{ span: 24 }}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col   span={18}>
              <Form.Item
                label={translation('Digital Twin Name')}
                name="Name"
                rules={[
                  {
                    required: true,
                    message: translation('Enter Digital Twin Name')
                  }
                ]}>
                <Input
                  disabled={isEditMode}
                  placeholder={translation('Please enter Digital Twin Name')}
                />
              </Form.Item>
              <Form.Item
                label={translation('Digital Twin Type')}
                name="Type"
                rules={[
                  {
                    required: true,
                    message: translation('Please Select Digital Twin Type')
                  }
                ]}>
                <Select disabled={isEditMode} placeholder={translation('Select Digital Twin Type')}>
                  {selectData.map((item) => (
                    <Select.Option key={item.Key} value={item.Value}>
                      {item.Value}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <div>{translation('Enter Digital Twin JSON')}</div>
              <Editor
                width="1000px"
                height="60vh"
                theme="vs-dark"
                defaultLanguage="json"
                value={editEditor}
                onChange={handleEditorChange}
                onMount={handleEditorDidMount}
              />
            </Col>
          </Row>
        </Form>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingTop: '20px'
          }}>
          <Button
            type="primary"
            shape="round"
            onClick={handleCreateClick}
            style={{ marginRight: '10px', marginTop: '10px' }}>
            {isEditMode ? translation('Update') : translation('Create')}
          </Button>
          {isEditMode ? (
            <Button
              type="primary"
              shape="round"
              onClick={handleDeleteClick}
              style={{ marginRight: '10px', marginTop: '10px' }}>
              {translation('Delete')}
            </Button>
          ) : null}
        </div>
      </Drawer>
      <Tabs defaultActiveKey="1" items={items} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  translationCache: state.TranslationReducer.translationCache
});

export default connect(mapStateToProps)(DigitalTwinConfiguration);
